import { useCallback, useEffect, useRef } from 'react';

import { normalizeWheelEventDirection } from '../utils/normalizeWheelEventDirection';

export type UseMouseWheelZoomOptions = {
  zoomIn: () => void;
  zoomOut: () => void;
};
/**
 * This hook calls zoomIn/zoomOut method when user uses wheel to scroll with CMD key pressed.
 * IT is taken from pdf.js/web/app.js #webViewerWheel
 */
export const useMouseWheelZoom = ({ zoomOut, zoomIn }: UseMouseWheelZoomOptions) => {
  const zoomInRef = useRef(zoomIn);
  zoomInRef.current = zoomIn;

  const zoomOutRef = useRef(zoomOut);
  zoomOutRef.current = zoomOut;

  const wheelUnusedTicks = useRef(0);

  const accumulateTicks = useCallback((ticks: number) => {
    // If the direction changed, reset the accumulated ticks.
    if ((wheelUnusedTicks.current > 0 && ticks < 0) || (wheelUnusedTicks.current < 0 && ticks > 0)) {
      wheelUnusedTicks.current = 0;
    }
    wheelUnusedTicks.current += ticks;
    const wholeTicks = Math.trunc(wheelUnusedTicks.current);
    wheelUnusedTicks.current -= wholeTicks;
    return wholeTicks;
  }, []);

  useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      const isCMDPressed = event.metaKey || event.ctrlKey;

      if (!isCMDPressed) return;

      event.preventDefault();
      event.stopPropagation();

      // It is important that we query deltaMode before delta{X,Y}, so that
      // Firefox doesn't switch to DOM_DELTA_PIXEL mode for compat with other
      // browsers, see https://bugzilla.mozilla.org/show_bug.cgi?id=1392460.
      const deltaMode = event.deltaMode;

      const delta = normalizeWheelEventDirection(event);

      let ticks = 0;
      if (deltaMode === WheelEvent.DOM_DELTA_LINE || deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        // For line-based devices, use one tick per event, because different
        // OSs have different defaults for the number lines. But we generally
        // want one "clicky" roll of the wheel (which produces one event) to
        // adjust the zoom by one step.
        if (Math.abs(delta) >= 1) {
          ticks = Math.sign(delta);
        } else {
          // If we're getting fractional lines (I can't think of a scenario
          // this might actually happen), be safe and use the accumulator.
          ticks = accumulateTicks(delta);
        }
      } else {
        // pixel-based devices
        const PIXELS_PER_LINE_SCALE = 30;
        ticks = accumulateTicks(delta / PIXELS_PER_LINE_SCALE);
      }

      if (ticks > 0) {
        zoomInRef.current();
      } else if (ticks < 0) {
        zoomOutRef.current();
      }
    };

    document.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      document.removeEventListener('wheel', onWheel);
    };
  }, [accumulateTicks]);
};
